import { useState } from "react";
import { Button, Tooltip, CircularProgress } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

// Row cap for CSV export — kept in sync with the backend EXPORT_ROW_CAP. Past
// this the button is disabled and the user is asked to narrow their search, so
// the export stays fast on the slow corporate network.
export const CSV_EXPORT_ROW_CAP = 20000;

/**
 * "Download CSV" control for the server-paginated lists. Exports the FULL result
 * set matching the active search + sort (built on the backend, streamed), not
 * just the loaded page.
 *
 * Disabled while the page is loading, when there are no rows, or when the total
 * exceeds the cap (with an explanatory tooltip); shows a spinner while the
 * download request is in flight.
 *
 * @param {Function} onExport  () => Promise — triggers the download.
 * @param {number}   rowCount  total rows across all pages (already known).
 * @param {boolean}  [loading] page is still loading (rows not yet available).
 * @param {number}   [cap]     max exportable rows (defaults to the shared cap).
 */
export function ExportCsvButton({
  onExport,
  rowCount = 0,
  loading = false,
  cap = CSV_EXPORT_ROW_CAP,
}) {
  const [exporting, setExporting] = useState(false);

  const overCap = rowCount > cap;
  const noRows = !rowCount;
  const disabled = loading || exporting || noRows || overCap;

  const tooltip = overCap
    ? `Too many rows to export (${rowCount.toLocaleString()}). Narrow your search to ${cap.toLocaleString()} or fewer.`
    : noRows
    ? "No rows to export"
    : "Download all rows matching the current filters";

  async function handleClick() {
    if (disabled) return;
    setExporting(true);
    try {
      await onExport();
    } catch (error) {
      // downloadCsv decodes the backend message onto error.message.
      console.error("CSV export failed:", error?.message || error);
    } finally {
      setExporting(false);
    }
  }

  return (
    // A disabled MUI button doesn't fire hover events, so wrap it so the
    // "narrow your search" tooltip still shows when the button is disabled.
    <Tooltip title={tooltip}>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleClick}
          disabled={disabled}
          sx={{ borderRadius: "10px", whiteSpace: "nowrap" }}
        >
          {exporting ? "Preparing…" : "Download CSV"}
        </Button>
      </span>
    </Tooltip>
  );
}
